import dayjs, { type Dayjs } from "dayjs";
import { produce } from "immer";
import wretch from "wretch";
import QueryStringAddon from "wretch/addons/queryString";
import { z } from "zod";
import { baseAPIURL, joinURL } from "../../utils";

type ListSessionsOptions = {
	/**
	 *Default: live
Allowed: past ┃ live
The type of session to query:

past — A past session that occurred during the specified date range.
live — A live sessions.
This parameter defaults to live.

Examples: past
	 */
	type?: "past" | "live";
	/**
	 * The start date to query, in yyyy-mm-dd format.

The ranges defined in the from and to parameters should only be a one month range. This is because the report only includes one month's worth of data.

Examples: 2021-12-01
	 */
	from: string | number | Date | Dayjs;
	/**The end date to query, in yyyy-mm-dd format.

The ranges defined in the from and to parameters should only be a one month range. This is because the report only includes one month's worth of data.

Examples: 2021-12-02 */
	to: string | number | Date | Dayjs;
	/**Default: 30
Max 300
The number of records returned within a single API call.

Examples: 10 */
	page_size?: number;
	/**
	 * The next page token is used to paginate through large result sets. A next page token will be returned whenever the set of available results exceeds the current page size. The expiration period for this token is 15 minutes.

Examples: Usse957pzxvmYwlmCZ50a6CNXFrhztxuj82
	 */
	next_page_token?: string;
	/**
	 * The next page token is used to paginate through large result sets. A next page token will be returned whenever the set of available results exceeds the current page size. The expiration period for this token is 15 minutes.

Examples: Usse957pzxvmYwlmCZ50a6CNXFrhztxuj82
	 */
	session_key?: string;
	/**
	 * The session's name.

Examples: My session
	 */
	session_name?: string;
};

export const validFrom = (from: string) => {
	return dayjs(from).isValid();
};

const listSessionsResultSchema = z
	.object({
		from: z.string(),
		to: z.string(),
		page_size: z.number(),
		next_page_token: z.string(),
		sessions: z.array(
			z.object({
				id: z.string(),
				session_name: z.string(),
				start_time: z.string(),
				end_time: z.string(),
				duration: z.string(),
				user_count: z.number(),
				has_voip: z.boolean(),
				has_video: z.boolean(),
				has_screen_share: z.boolean(),
				has_recording: z.boolean(),
				has_pstn: z.boolean(),
				has_session_summary: z.boolean(),
			}),
		),
	})
	.deepPartial();

type ListSessionsResult = z.infer<typeof listSessionsResultSchema>;

const listSessions = async (
	token: string,
	options: ListSessionsOptions,
): Promise<ListSessionsResult> => {
	const validOption = produce(options, (draft) => {
		if (draft.type !== "past" && draft.type !== "live") {
			draft.type = "live";
		}
		draft.from = dayjs(draft.from).format("YYYY-MM-DD");
		draft.to = dayjs(draft.to).format("YYYY-MM-DD");
		if (draft.page_size) {
			if (draft.page_size < 1) {
				draft.page_size = 1;
			} else if (draft.page_size > 300) {
				draft.page_size = 300;
			}
		} else {
			draft.page_size = 30;
		}
	});
	const url = joinURL({
		base: baseAPIURL,
		pathList: ["v2", "videosdk", "sessions"],
	});
	const w = await wretch(url)
		.addon(QueryStringAddon)
		.query(validOption)
		.auth(`Bearer ${token}`)
		.get()
		.json()
		.catch((e) => {
			throw new Error(e.text);
		});
	const parsed = listSessionsResultSchema.parse(w);
	return parsed;
};

export const curriedListSessions = (token: string) => (
	options: ListSessionsOptions,
) => listSessions(token, options);